
import React, { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import { usePassword } from '@/contexts';
import LockoutDisplay from './LockoutDisplay';
import { ResetPasswordForm } from './password/PasswordForms';
import PassKeyResetForm from './password/PassKeyResetForm';
import ProtectionCard from './password-protection/ProtectionCard';
import MainLoginForm from './password-protection/MainLoginForm';

interface PasswordProtectionProps {
  onAuthenticated: () => void;
  showPassKeyForm?: boolean;
  onShowPassKeyForm?: () => void;
  onBackFromPassKey?: () => void;
  children?: React.ReactNode;
}

const PasswordProtection: React.FC<PasswordProtectionProps> = ({ 
  onAuthenticated,
  showPassKeyForm: externalShowPassKeyForm,
  onShowPassKeyForm,
  onBackFromPassKey,
  children 
}) => {
  const { hasPassword, isLocked, lockoutEndTime, remainingAttempts, verifyPassword } = usePassword();
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [showResetForm, setShowResetForm] = useState(false);
  const [internalShowPassKeyForm, setInternalShowPassKeyForm] = useState(false);
  
  // Determine whether to show pass key form from either internal state or prop
  const showPassKeyForm = externalShowPassKeyForm || internalShowPassKeyForm;
  
  useEffect(() => {
    if (!hasPassword) {
      onAuthenticated();
    }
  }, [hasPassword, onAuthenticated]);

  // If external state changes, update internal state
  useEffect(() => {
    if (externalShowPassKeyForm !== undefined) {
      setInternalShowPassKeyForm(externalShowPassKeyForm);
    }
  }, [externalShowPassKeyForm]);

  const handlePasswordSubmit = async (password: string) => {
    setIsLoading(true);
    setError('');
    
    try {
      if (verifyPassword(password)) {
        onAuthenticated();
      } else {
        setError('Incorrect password');
      }
    } catch (error) {
      setError('An error occurred while verifying the password');
    } finally {
      setIsLoading(false);
    }
  };

  const handleShowResetForm = () => {
    setShowResetForm(true);
    setInternalShowPassKeyForm(false);
  };

  const handleShowPassKeyForm = () => {
    console.log("Showing passkey form");
    setInternalShowPassKeyForm(true);
    setShowResetForm(false);
    // Call external handler if provided
    if (onShowPassKeyForm) {
      onShowPassKeyForm();
    }
  };

  const handleBackToMain = useCallback(() => {
    console.log("Handling back to main");
    setShowResetForm(false);
    setInternalShowPassKeyForm(false);
    
    // Call external handler if provided
    if (onBackFromPassKey) {
      console.log("Calling external back handler from PasswordProtection");
      onBackFromPassKey();
    }
  }, [onBackFromPassKey]);

  const handlePassKeyAuthenticated = () => {
    // Ensure we handle the authentication success correctly
    console.log("PassKey authentication successful");
    onAuthenticated();
  };

  if (!hasPassword) {
    return <>{children}</>;
  }

  // Convert lockoutEndTime to Date if it's a string
  const lockoutEndTimeDate = lockoutEndTime 
    ? typeof lockoutEndTime === 'string' 
      ? new Date(lockoutEndTime) 
      : lockoutEndTime
    : null;

  return (
    <div className="fixed inset-0 cyber-backdrop flex items-center justify-center z-[100] p-4">
      <motion.div 
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="w-full max-w-md mx-auto"
      >
        {isLocked && lockoutEndTimeDate ? (
          <div className="cyber-card p-8 text-center space-y-6">
            <div className="cyber-corners cyber-corners-tl cyber-corners-tr cyber-corners-bl cyber-corners-br">
              <LockoutDisplay lockoutEndTime={lockoutEndTimeDate} />
            </div>
          </div>
        ) : (
          <ProtectionCard>
            {/* Main Login Form */}
            {!showResetForm && !showPassKeyForm && (
              <MainLoginForm
                onSubmit={handlePasswordSubmit}
                error={error}
                remainingAttempts={remainingAttempts}
                isLoading={isLoading}
                onShowResetForm={handleShowResetForm}
                onShowPassKeyForm={handleShowPassKeyForm}
              />
            )}

            {/* Reset Password Form */}
            {showResetForm && (
              <div className="text-left">
                <h2 className="text-2xl font-bold cyber-text-glow mb-6 text-center">Reset Password</h2>
                <ResetPasswordForm onBack={handleBackToMain} />
              </div>
            )}

            {/* VIP Pass Key Form */}
            {showPassKeyForm && (
              <div className="text-left">
                <h2 className="text-2xl font-bold cyber-text-glow mb-6 text-center">VIP Emergency Access</h2>
                <PassKeyResetForm 
                  onBack={handleBackToMain} 
                  onAuthenticated={handlePassKeyAuthenticated} 
                />
              </div>
            )}
          </ProtectionCard>
        )}
      </motion.div>
    </div>
  );
};

export default PasswordProtection;
